<template>
  <!--
    Avatar-style ownership indicator shown in listings (automation, bulk,
    templates, lists, custom data, events).
    Renders the integration logo in a small bordered circle, with a soft
    "linked-by-integration" ring around it. Hovering reveals the full
    integration name + a status sentence.

    Why this shape rather than a labelled chip:
      - Scales to N integrations without bloating every row with their names.
      - Visual signature of the integration is the partner's logo, not a
        purple background. Two integrations side-by-side stay readable.
      - Keeps the row dense — the badge sits at the same width whatever
        the integration name length.
  -->
  <span
    v-if="ownerIntegration"
    v-tooltip.top="tooltipLabel"
    class="integration-owner-badge"
    :class="{ 'integration-owner-badge--inactive': !ownerIntegration.is_active }"
  >
    <span class="integration-owner-badge__avatar">
      <img
        v-if="ownerIntegration.icon_url"
        :src="ownerIntegration.icon_url"
        :alt="ownerIntegration.name"
        class="integration-owner-badge__icon"
      >
      <i
        v-else
        class="fa-solid fa-plug integration-owner-badge__icon-fallback"
      />
    </span>
    <!-- Visually hidden text for screen readers + keyboard users. -->
    <span class="integration-owner-badge__sr">
      {{ ownerIntegration.name || t('integrationOwnerBadge.unknown') }}
    </span>
  </span>
</template>

<script lang="ts">
import { defineComponent, PropType, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { OwnerIntegrationInfo } from '@/types/integrations';

// Re-exported so existing imports keep working during the migration to
// `@/types/integrations`.
export type { OwnerIntegrationInfo };

export default defineComponent({
  name: 'IntegrationOwnerBadge',

  props: {
    ownerIntegration: {
      type: Object as PropType<OwnerIntegrationInfo | null>,
      default: null,
    },
  },

  setup(props) {
    const { t } = useI18n();

    const tooltipLabel = computed(() => {
      if (!props.ownerIntegration) return '';
      if (!props.ownerIntegration.is_active) {
        return t('integrationOwnerBadge.uninstalled', {
          name: props.ownerIntegration.name,
        });
      }
      return t('integrationOwnerBadge.tooltip', {
        name: props.ownerIntegration.name,
      });
    });

    return { t, tooltipLabel };
  },
});
</script>

<style scoped>
/* Outer wrapper. 22px matches the visual weight of inline icons used in
   neighbouring rows (calendar / edit icons sit at ~16-18px font-size, so
   a 22px disc next to a heading reads as the same row height without the
   halo bloat that 28px caused). */
.integration-owner-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  vertical-align: middle;
  cursor: help;
  flex-shrink: 0;
  margin-right: 0.5rem !important;
}

/* The avatar itself — small white-bg disc with a thin purple ring.
   The ring colour signals integration provenance even at a glance. */
.integration-owner-badge__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  border-radius: 9999px;
  background: #ffffff;
  border: 1.5px solid #c4b5fd;
  overflow: hidden;
  transition: border-color 0.15s ease, transform 0.15s ease;
  box-sizing: border-box;
}

.integration-owner-badge:hover .integration-owner-badge__avatar {
  border-color: #8b5cf6;
  transform: scale(1.08);
}

.integration-owner-badge__icon {
  width: 12px;
  height: 12px;
  object-fit: contain;
  display: block;
}

.integration-owner-badge__icon-fallback {
  font-size: 0.65rem;
  color: #7c3aed;
}

/* Inactive variant — colours desaturated, no animation. */
.integration-owner-badge--inactive .integration-owner-badge__avatar {
  border-color: #d1d5db;
  filter: grayscale(0.85) opacity(0.75);
}

.integration-owner-badge--inactive:hover .integration-owner-badge__avatar {
  border-color: #9ca3af;
  transform: none;
}

.integration-owner-badge--inactive .integration-owner-badge__icon-fallback {
  color: #9ca3af;
}

/* Screen-reader-only label. */
.integration-owner-badge__sr {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
